import { SanityProduct } from "@graphql-types";
import { useStore } from "@state/store";
import { toast } from "react-toastify";
import { shopifyGraphQLClient, makeGraphQLRequest } from "./shopifyGraphQLClient";
import {
  CART_CREATE,
  CART_ADD,
  CART_LINES_REMOVE,
  CART_LINES_UPDATE,
  PRODUCTS_COLLECTION_QUERY,
  PRODUCT_QUERY,
} from "./shopifyGraphQLClient";
import {
  CartCreateResponse,
  CartLinesAddResponse,
  CartLinesRemoveResponse,
  CartLinesUpdateResponse,
  ProductsCollectionResponse,
  ProductQueryResponse,
  Cart,
  Product,
} from "./shopifyTypes";

export const createCheckout = async () => {
  try {
    const response = await makeGraphQLRequest<CartCreateResponse>(CART_CREATE, {});

    if (response.cartCreate.userErrors.length > 0) {
      console.error("Cart creation errors:", response.cartCreate.userErrors);
      throw new Error(response.cartCreate.userErrors[0].message);
    }

    const cart = response.cartCreate.cart;
    useStore.setState({ checkout: cart });

    return cart;
  } catch (error) {
    console.error("Error creating cart:", error);
    toast.error("Failed to create cart. Please try again.");
    throw error;
  }
};

export const addItemToCart = async (cartId: string, variantId: string, quantity: number) => {
  if (variantId == null) {
    return null;
  }

  try {
    const lines = [
      {
        merchandiseId: variantId,
        quantity: quantity ?? 1,
      },
    ];

    const response = await makeGraphQLRequest<CartLinesAddResponse>(CART_ADD, {
      cartId,
      lines,
    });

    if (response.cartLinesAdd.userErrors.length > 0) {
      console.error("Add item to cart errors:", response.cartLinesAdd.userErrors);
      throw new Error(response.cartLinesAdd.userErrors[0].message);
    }

    const cart = response.cartLinesAdd.cart;
    useStore.setState({ checkout: cart });

    toast.success("Item added to cart successfully!");
    return cart;
  } catch (error) {
    console.error("Error adding item to cart:", error);
    toast.error("Failed to add item to cart. Please try again.");
    throw error;
  }
};

export const getAllProducts = async () => {
  try {
    const response = await makeGraphQLRequest<ProductsCollectionResponse>(PRODUCTS_COLLECTION_QUERY, {
      first: 250, // Fetch up to 250 products
    });

    return response.products.edges.map(edge => edge.node);
  } catch (error) {
    console.error("Error fetching all products:", error);
    throw error;
  }
};

export const getProduct = async (lineItem: SanityProduct) => {
  try {
    const products = await getAllProducts();
    const filteredProduct = products.filter((product: Product) => {
      return product.variants?.edges?.[0]?.node?.sku === lineItem.productCode;
    });
    return filteredProduct;
  } catch (error) {
    console.error("Error getting product:", error);
    throw error;
  }
};

// Additional helper function to get a single product by ID
export const getProductById = async (productId: string) => {
  try {
    const response = await makeGraphQLRequest<ProductQueryResponse>(PRODUCT_QUERY, {
      id: productId,
    });

    return response.node;
  } catch (error) {
    console.error("Error getting product by ID:", error);
    throw error;
  }
};

export const removeItemFromCart = async (cartId: string, lineItemId: string) => {
  if (lineItemId == null) {
    return null;
  }

  try {
    const response = await makeGraphQLRequest<CartLinesRemoveResponse>(CART_LINES_REMOVE, {
      cartId,
      lineIds: [lineItemId],
    });

    if (response.cartLinesRemove.userErrors.length > 0) {
      console.error("Remove item from cart errors:", response.cartLinesRemove.userErrors);
      throw new Error(response.cartLinesRemove.userErrors[0].message);
    }

    const cart = response.cartLinesRemove.cart;
    useStore.setState({ checkout: cart });

    toast.success("Item removed from cart successfully!");
    return cart;
  } catch (error) {
    console.error("Error removing item from cart:", error);
    toast.error("Failed to remove item from cart. Please try again.");
    throw error;
  }
};

export const updateLineItemQuanity = async (cartId: string, lineItemId: string, quantity: number) => {
  if (lineItemId == null) {
    return null;
  }

  try {
    const lines = [
      {
        id: lineItemId,
        quantity: quantity ?? 1,
      },
    ];

    const response = await makeGraphQLRequest<CartLinesUpdateResponse>(CART_LINES_UPDATE, {
      cartId,
      lines,
    });

    if (response.cartLinesUpdate.userErrors.length > 0) {
      console.error("Update line item quantity errors:", response.cartLinesUpdate.userErrors);
      throw new Error(response.cartLinesUpdate.userErrors[0].message);
    }

    const cart = response.cartLinesUpdate.cart;
    useStore.setState({ checkout: cart });

    toast.success("Cart updated successfully!");
    return cart;
  } catch (error) {
    console.error("Error updating line item quantity:", error);
    toast.error("Failed to update cart. Please try again.");
    throw error;
  }
};
